import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { Table, Checkbox, message, Card, Row, Col, Input, Button } from "antd";
import { API_URL } from "../../url_config";
import { NavigationStepsAll } from "../../components/navbars/navbar_all";
import { EditOutlined } from "@ant-design/icons";
import ModifyItemPage from "../../components/modify_page";
import { debounce } from "lodash";

const { TextArea } = Input;

// === Style du tableau ===
const tableStyle = document.createElement("style");
tableStyle.innerHTML = `
  .custom-row td {
    border-bottom: 2px solid #d9d9d9 !important;
  }
  .ant-table-thead > tr > th {
    background-color: #f5f5f5 !important;
    font-weight: 600;
    border-bottom: 2px solid #bfbfbf !important;
    color: #333;
  }
  .ant-table-tbody > tr:hover > td {
    background-color: #f0f0f0 !important;
  }
  .ant-table {
    border-collapse: separate !important;
    border-spacing: 0;
  }
`;
document.head.appendChild(tableStyle);

// === Champ fluide (Commentaire / Aide) ===
const TablePoSiel = ({ record, updateLocal }) => {
  const [comment, setComment] = useState(record.comment || "");
  const [aide, setAide] = useState(record.aide || "");

  useEffect(() => setComment(record.comment || ""), [record.comment]);
  useEffect(() => setAide(record.aide || ""), [record.aide]);

  const handleChange = (field, value) => {
    if (field === "comment") setComment(value);
    if (field === "aide") setAide(value);
    updateLocal(record.id, field, value);
  };

  return (
    <div style={{ padding: 20 }}>
      <Row gutter={16}>
        <Col span={12}>
          <Card title="Commentaire" bordered>
            <TextArea
              rows={4}
              value={comment}
              onChange={(e) => handleChange("comment", e.target.value)}
              placeholder="Saisis un commentaire..."
            />
          </Card>
        </Col>
        <Col span={12}>
          <Card
            title={<span style={{ fontSize: 12, color: "#888" }}>Informations (Supervision)</span>}
            bordered
          >
            <TextArea
              rows={4}
              value={aide}
              onChange={(e) => handleChange("aide", e.target.value)}
              placeholder="Décris ton besoin d’aide..."
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

// === Hook personnalisé : gestion des données + syncro toutes les 8s ===
const usePoSielData = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const debouncedPatchRef = useRef(
    debounce(async (id, field, value) => {
      try {
        await axios.patch(`${API_URL}/validations/validation_po_siel/${id}/`, { [field]: value });
      } catch (error) {
        console.error("Erreur PATCH:", error);
        message.error("Erreur lors de la mise à jour serveur");
      }
    }, 700)
  );

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/validations/validation_po_siel/`);
      setData(res.data.map((item, index) => ({ key: index, ...item })));
    } catch (error) {
      console.error("Erreur de chargement :", error);
      message.error("Erreur de chargement des données");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 8000); // syncro toutes les 8 secondes
    return () => clearInterval(interval);
  }, [fetchData]);

  const updateLocal = useCallback((id, field, value) => {
    setData((prev) => prev.map((item) => (item.id === id ? { ...item, [field]: value } : item)));
    debouncedPatchRef.current(id, field, value);
  }, []);

  useEffect(() => {
    return () => debouncedPatchRef.current.cancel();
  }, []);

  return { data, loading, updateLocal, fetchData };
};

// === Composant principal ===
const ValidationPoSielAll = () => {
  const { data, loading, updateLocal } = usePoSielData();

  const columns = [
    { title: "Pupitre", dataIndex: "pupitre", key: "pupitre" },
    {
      title: "Ligne",
      dataIndex: "ligne",
      key: "ligne",
      render: (_, record) => {
        const current = data.find((d) => d.key === record.key);
        const value = current ? current.ligne : record.ligne;

        return (
          <Input
            value={value}
            onChange={(e) => updateLocal(record.id, "ligne", e.target.value)}
            placeholder="Modifier la ligne"
            style={{ width: "100%" }}
          />
        );
      },
    },
    {
      title: "Présence ligne sur PO",
      dataIndex: "presence_ligne_sur_po",
      render: (val, record) => (
        <Checkbox
          checked={val}
          onChange={(e) => updateLocal(record.id, "presence_ligne_sur_po", e.target.checked)}
        />
      ),
    },
    {
      title: "Chargement TM",
      dataIndex: "chargement_tm",
      render: (val, record) => (
        <Checkbox
          checked={val}
          onChange={(e) => updateLocal(record.id, "chargement_tm", e.target.checked)}
        />
      ),
    },
    {
      title: "Présence synoptique",
      dataIndex: "presence_synoptique",
      render: (val, record) => (
        <Checkbox
          checked={val}
          onChange={(e) => updateLocal(record.id, "presence_synoptique", e.target.checked)}
        />
      ),
    },
    {
      title: "ETAT",
      dataIndex: "signal",
      render: (val, record) => {
        const color = val === 1 ? "#4CAF50" : val === -1 ? "#FF4D4F" : "#BFBFBF";
        const symbol = val === 1 ? "✓" : val === -1 ? "✗" : " ";
        const handleClick = () => {
          const nextVal = val === 0 ? 1 : val === 1 ? -1 : 0;
          updateLocal(record.id, "signal", nextVal);
        };
        return (
          <div
            onClick={handleClick}
            style={{ cursor: "pointer", display: "flex", justifyContent: "center", alignItems: "center" }}
          >
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: 6,
                backgroundColor: color,
                color: "#fff",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                fontWeight: "bold",
                fontSize: 16,
                userSelect: "none",
                transition: "all 0.3s ease",
              }}
            >
              {symbol}
            </div>
          </div>
        );
      },
    },
  ];

  return (
    <div style={{ padding: 20 }}>
      <NavigationStepsAll selectedKey="po_siel" />
      <Table
        columns={columns}
        dataSource={data}
        loading={loading}
        expandable={{
          expandedRowRender: (record) => <TablePoSiel record={record} updateLocal={updateLocal} />,
        }}
        bordered
        style={{
          border: "2px solid #bfbfbf",
          borderRadius: 12,
          overflow: "hidden",
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
        }}
        rowClassName={() => "custom-row"}
        pagination={false}
      />

      <div style={{ padding: 20 }}>
        <ModifyItemPage
          apiUrl={`${API_URL}/validations/validation_po_siel/`}
          ot_tag="cds"
          title="Gestion"
          rowKey="id"
          trigger={<Button icon={<EditOutlined />} shape="circle" />}
          columns={[{ title: "Pupitre", dataIndex: "pupitre", key: "pupitre" }]}
          formFields={[{ name: "pupitre", label: "Pupitre", placeholder: "Entrez le pupitre" }]}
        />
      </div>
    </div>
  );
};

export default ValidationPoSielAll;
